import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutGrid, LayoutList, Search, X, ChevronDown, Package } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import ShopProducts from '../products/ShopProducts';
import { setStoreCategoryFilter, setStoreSearchKeyword, fetchProductsBySeller } from '../../store/reducers/homeReducer';

const StoreProductList = ({ settings, seller }) => {
    const dispatch = useDispatch();
    const { products, totalProducts, parPage, storeSearchKeyword, storeCategoryFilter, loading } = useSelector(
        (state) => state.home
    );

    const [styles, setStyles] = useState("grid");
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [activeTab, setActiveTab] = useState('products');
    const [storeCategories, setStoreCategories] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Extract categories from products
    useEffect(() => {
        if (products) {
            const uniqueCategories = [...new Set(products.map(p => p.category))];
            setStoreCategories(uniqueCategories.map(c => ({ name: c })));
        }
    }, [products]);

    // Fetch products
    useEffect(() => {
        if (seller && seller._id) {
            dispatch(fetchProductsBySeller({ sellerId: seller._id, pageNumber }));
        }
    }, [seller?._id, pageNumber, dispatch]);

    // Accumulate products
    useEffect(() => {
        if (products) {
            if (products.length < parPage) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }

            if (pageNumber === 1) {
                setAllProducts(products);
            } else {
                setAllProducts(prev => {
                    const newProducts = products.filter(p => !prev.some(existing => existing._id === p._id));
                    return [...prev, ...newProducts];
                });
            }
        }
    }, [products, pageNumber, parPage]);

    // Filter products
    useEffect(() => {
        if (allProducts) {
            let tempProducts = [...allProducts];

            if (storeSearchKeyword) {
                tempProducts = tempProducts.filter(p =>
                    p.name.toLowerCase().includes(storeSearchKeyword.toLowerCase())
                );
            }

            if (storeCategoryFilter) {
                tempProducts = tempProducts.filter(p =>
                    p.category === storeCategoryFilter
                );
            }

            setFilteredProducts(tempProducts);
        }
    }, [allProducts, storeSearchKeyword, storeCategoryFilter]);

    // Infinite Scroll
    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPageNumber(prev => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    // Reset pagination when filters change
    useEffect(() => {
        setPageNumber(1);
        setAllProducts([]);
        setHasMore(true);
    }, [storeSearchKeyword, storeCategoryFilter]);

    if (!seller) return null;

    return (
        <section className="pt-8 pb-4 w-[80%] md:w-[90%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
            <div className="pl-8 md:pl-0 md:w-full w-[100%] text-sm ">
                <div className="flex flex-col gap-2 mb-6">
                    <h1 className="text-4xl font-bold text-[var(--store-text)] flex items-center gap-3">
                        {seller.shopInfo?.shopName?.replace(/-/g, ' ')}
                        {seller.verification?.verified && (
                            <img className="w-[30px] h-auto" src={`${process.env.REACT_APP_URL}/images/checkmark.svg`} alt="Verified" />
                        )}
                    </h1>
                </div>

                <div className="md-lg:flex hidden w-full mb-4 gap-2 items-center">
                    <div className="flex-1 relative h-[40px]">
                        <div className={`absolute inset-0 transition-all duration-300 ease-in-out ${isSearchMode ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}>
                            <Select onValueChange={(value) => dispatch(setStoreCategoryFilter(value === "All" ? "" : value))}>
                                <SelectTrigger icon={ChevronDown} className="w-full text-[var(--store-select-text)] bg-[var(--store-select-bg)] font-semibold px-5 h-[40px] border-[var(--store-select-border)] store-select-focus"
                                    style={{
                                        borderRadius: settings?.inputRadius ? `${settings.inputRadius}px` : 'var(--store-select-radius, 20px)',
                                        borderWidth: 'var(--store-select-border-width, 1px)',
                                        backgroundColor: settings?.inputBg,
                                        color: settings?.inputText,
                                        borderColor: settings?.inputBorder
                                    }}>
                                    <SelectValue placeholder="Browse collections" />
                                </SelectTrigger>
                                <SelectContent className="nex_drop z-[100000] bg-[var(--store-card-bg)] border-[var(--store-border)]">
                                    <SelectItem value="All" className="hover:text-[var(--store-text)] cursor-pointer text-[var(--store-text)]">All Categories</SelectItem>
                                    {storeCategories.map((c, i) => (
                                        <SelectItem key={i} value={c.name} className="hover:text-[var(--store-text)] cursor-pointer text-[var(--store-text)]">
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className={`absolute inset-0 transition-all duration-300 ease-in-out ${!isSearchMode ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}>
                            <Input
                                className="h-[40px] w-full bg-[var(--store-input-bg)] px-5 placeholder:text-[var(--store-input-text)] text-[var(--store-input-text)] border-[var(--store-input-border)] store-input-focus"
                                style={{
                                    borderRadius: settings?.inputRadius ? `${settings.inputRadius}px` : 'var(--store-input-radius, 20px)',
                                    borderWidth: 'var(--store-input-border-width, 1px)',
                                    backgroundColor: settings?.inputBg,
                                    color: settings?.inputText,
                                    borderColor: settings?.inputBorder
                                }}
                                onChange={(e) => dispatch(setStoreSearchKeyword(e.target.value))}
                                type="text"
                                placeholder={`Search in ${seller.shopInfo?.shopName?.replace(/-/g, ' ')}`}
                            />
                        </div>
                    </div>
                    <div
                        onClick={() => {
                            setIsSearchMode(!isSearchMode);
                            if (isSearchMode) {
                                dispatch(setStoreSearchKeyword(""));
                            }
                        }}
                        className="h-[40px] w-[40px] flex items-center justify-center bg-[var(--store-card-bg)] rounded-full cursor-pointer hover:bg-[var(--store-glass)] transition-colors border border-[var(--store-input-border)]"
                        style={{
                            backgroundColor: settings?.searchBtnBg,
                            borderColor: settings?.searchBtnBorder
                        }}
                    >
                        {isSearchMode ?
                            <X className={`text-[var(--store-icon)] ${settings?.searchBtnColor ? '' : ''}`} style={{ color: settings?.searchBtnColor }} size={20} /> :
                            <Search className={`text-[var(--store-icon)] ${settings?.searchBtnColor ? '' : ''}`} style={{ color: settings?.searchBtnColor }} size={20} />
                        }
                    </div>
                </div>

                <div className="flex gap-3 mb-6 mt-4">
                    <button
                        onClick={() => setActiveTab('products')}
                        className={`px-6 py-2 rounded-full font-medium text-sm transition-all border ${activeTab === 'products'
                            ? 'bg-[var(--store-glass)] backdrop-blur-md border-[var(--store-border)] text-[var(--store-text)] shadow-sm'
                            : 'bg-transparent border-transparent text-[var(--store-muted)] hover:text-[var(--store-text)] hover:bg-[var(--store-glass-hover)]'
                            }`}
                        style={{
                            backgroundColor: activeTab === 'products' ? settings?.tabActiveBg : undefined,
                            color: activeTab === 'products' ? settings?.tabActiveText : settings?.tabInactiveText,
                            borderColor: activeTab === 'products' ? settings?.tabBorder : 'transparent'
                        }}
                    >
                        Products
                    </button>
                    <button
                        onClick={() => setActiveTab('about')}
                        className={`px-6 py-2 rounded-full font-medium text-sm transition-all border ${activeTab === 'about'
                            ? 'bg-[var(--store-glass)] backdrop-blur-md border-[var(--store-border)] text-[var(--store-text)] shadow-sm'
                            : 'bg-transparent border-transparent text-[var(--store-muted)] hover:text-[var(--store-text)] hover:bg-[var(--store-glass-hover)]'
                            }`}
                        style={{
                            backgroundColor: activeTab === 'about' ? settings?.tabActiveBg : undefined,
                            color: activeTab === 'about' ? settings?.tabActiveText : settings?.tabInactiveText,
                            borderColor: activeTab === 'about' ? settings?.tabBorder : 'transparent'
                        }}
                    >
                        About
                    </button>
                </div>

                {activeTab === 'about' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-lg font-medium py-4 flex items-center text-[var(--store-text)]">About store <Package className="ml-2" /></h1>
                        <p className="text-[var(--store-muted)] whitespace-pre-wrap break-words">{seller.shopInfo?.description}</p>
                    </div>
                )}

                {activeTab === 'products' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="lg-md:py-10 mb-10 rounded-md flex justify-between items-start">
                            <h2 className="text-lg font-medium text-[var(--store-text)]">
                                {settings?.title || "All Products"} ({totalProducts})
                            </h2>
                            <div className="flex justify-center items-center gap-3">
                                <div className="flex justify-center items-start gap-4 md-lg:hidden">
                                    <div
                                        onClick={() => setStyles("grid")}
                                        className={`p-2 ${styles === "grid" && "bg-[var(--store-card-bg)]"
                                            } text-[var(--store-icon)] cursor-pointer rounded-sm`}
                                        style={{
                                            backgroundColor: styles === "grid" ? (settings?.layoutIconBg || undefined) : undefined,
                                            color: settings?.layoutIconColor
                                        }}
                                    >
                                        <LayoutGrid size={18} />
                                    </div>
                                    <div
                                        onClick={() => setStyles("list")}
                                        className={`p-2 ${styles === "list" && "bg-[var(--store-card-bg)]"
                                            } text-[var(--store-icon)] cursor-pointer rounded-sm`}
                                        style={{
                                            backgroundColor: styles === "list" ? (settings?.layoutIconBg || undefined) : undefined,
                                            color: settings?.layoutIconColor
                                        }}
                                    >
                                        <LayoutList size={18} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="pb-8">
                            <ShopProducts products={filteredProducts} styles={styles} storeMode={true} customSettings={settings} />
                        </div>

                        <div ref={lastProductElementRef} className="w-full flex justify-center py-4 flex-col items-center gap-2">
                            {loading && (
                                <div className="w-8 h-8 border-4 border-[var(--store-text)] border-t-transparent rounded-full animate-spin"></div>
                            )}
                            {!loading && (allProducts.length >= totalProducts || !hasMore) && totalProducts > 0 && (
                                <span className="text-[var(--store-muted)] text-sm font-medium"></span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default StoreProductList;
